import { CommonModule } from '@angular/common';
import { Component, ComponentRef, Inject, ViewChild, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { ContextMenuItemEvent } from 'src/app/components/api-configurations/common/models/context-menu-item-event';
import { MultiLevelContextMenuItem } from 'src/app/components/api-configurations/common/models/multi-level-context-menu-item';
import { MultiLevelContextMenuComponent } from 'src/app/components/api-configurations/common/multi-level-context-menu/multi-level-context-menu.component';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { FormMode } from 'src/app/controls/common/form-mode';
import { NeedApiConfigurations } from 'src/app/controls/workflows/need-api-configurations';
import { NeedAttributes } from 'src/app/controls/workflows/need-attributes';
import { NeedCustomers } from 'src/app/controls/workflows/need-customers';
import { NeedEmailTemplates } from 'src/app/controls/workflows/need-email-templates';
import { NeedFolderStates } from 'src/app/controls/workflows/need-folder-states';
import { NeedSmsTemplates } from 'src/app/controls/workflows/need-sms-templates';
import { NeedSteps } from 'src/app/controls/workflows/need-steps';
import { NeedTimerTriggers } from 'src/app/controls/workflows/need-timer-triggers';
import { WorkflowActionActivateStepDataComponent } from 'src/app/controls/workflows/workflow-action-activate-step-data/workflow-action-activate-step-data.component';
import { WorkflowActionArchiveFolderDataComponent } from 'src/app/controls/workflows/workflow-action-archive-folder-data/workflow-action-archive-folder-data.component';
import { WorkflowActionCallApiDataComponent } from 'src/app/controls/workflows/workflow-action-call-api-data/workflow-action-call-api-data.component';
import { WorkflowActionChangeStepStateDataComponent } from 'src/app/controls/workflows/workflow-action-change-step-state-data/workflow-action-change-step-state-data.component';
import { WorkflowActionCompleteStepDataComponent } from 'src/app/controls/workflows/workflow-action-complete-step-data/workflow-action-complete-step-data.component';
import { WorkflowActionCompleteWorkflowDataComponent } from 'src/app/controls/workflows/workflow-action-complete-workflow-data/workflow-action-complete-workflow-data.component';
import { WorkflowActionReminderCreationDataComponent } from 'src/app/controls/workflows/workflow-action-reminder-creation-data/workflow-action-reminder-creation-data.component';
import { WorkflowActionSendMailDataComponent } from 'src/app/controls/workflows/workflow-action-send-mail-data/workflow-action-send-mail-data.component';
import { WorkflowActionSendSmsDataComponent } from 'src/app/controls/workflows/workflow-action-send-sms-data/workflow-action-send-sms-data.component';
import { WorkflowActionSwitchFolderAsUnreadDataComponent } from 'src/app/controls/workflows/workflow-action-switch-folder-as-unread-data/workflow-action-switch-folder-as-unread-data.component';
import { WorkflowActionTimerStopComponent } from 'src/app/controls/workflows/workflow-action-timer-stop/workflow-action-timer-stop.component';
import { WorkflowActionUpdateAttributeDataComponent } from 'src/app/controls/workflows/workflow-action-update-attribute-data/workflow-action-update-attribute-data.component';
import { WorkflowDataInterface } from 'src/app/controls/workflows/workflow-data.interface';
import { WorkflowNotImplementedActionDataComponent } from 'src/app/controls/workflows/workflow-not-implemented-action-data.component/workflow-not-implemented-action-data.component';
import { WorkflowActionTypes } from 'src/app/models/RestApiCaller/Enums/workflow-action-types';
import { WorkflowTriggerTypes } from 'src/app/models/RestApiCaller/Enums/workflow-trigger-types';
import { RestApiAction } from 'src/app/models/RestApiCaller/actions/rest-api-action';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { CustomersService } from 'src/app/services/customers.service';
import { EmailsTemplatesService } from 'src/app/services/emails-templates.service';
import { FoldersStatesService } from 'src/app/services/folders-states.service';
import { MemoryService } from 'src/app/services/memory.service';
import { RestApiConfigurationsService } from 'src/app/services/restApi/rest-api-configurations.service';
import { SmsTemplatesService } from 'src/app/services/sms-templates.service';
import { WorkflowDefinitionsService } from 'src/app/services/workflows/workflow-definitions.service';
import { ActionFormDialogData } from '../Models/action-form-dialog-data';
import { ActionFormDialogResult } from '../Models/action-form-dialog-result';

@Component({
    selector: 'app-action-form-dialog',
    imports: [
        MatCardModule,
        FormsModule,
        MatFormFieldModule,
        MatButtonModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MultiLevelContextMenuComponent,
        CommonModule,
        ReactiveFormsModule,
    ],
    templateUrl: './action-form-dialog.component.html',
    styleUrl: './action-form-dialog.component.scss',
})
export class ActionFormDialogComponent {
    /**
     * Liste des enums utilisés dans le template.
     */
    WorkflowActionTypes = WorkflowActionTypes;

    /**
     * Reference au container dans lequel est affiché les composants dynamiques.
     */
    @ViewChild('dynamicContainer', { read: ViewContainerRef })
    dynamicContainer!: ViewContainerRef;

    /**
     * Reference au composant du menu contextuel
     */
    @ViewChild(MultiLevelContextMenuComponent)
    contextMenu: MultiLevelContextMenuComponent;

    /**
     * Instance de l'action affichée dans le formulaire.
     */
    public item: RestApiAction;

    /**
     * Titre de la boite de dialogue.
     */
    public title: string;

    /**
     * Liste des différents types d'actions affichés dans le menu déroulant.
     */
    public actionsType: { value: WorkflowActionTypes; name: string }[] = [
        { value: WorkflowActionTypes.ActivateStep, name: 'Activer une étape' },
        { value: WorkflowActionTypes.ApiCall, name: 'Appeller une API' },
        { value: WorkflowActionTypes.TimerStop, name: 'Arrêt de minuteur' },
        { value: WorkflowActionTypes.ChangeStepState, name: "Changer l'état d'une étape" },
        { value: WorkflowActionTypes.CompleteStep, name: "Compléter l'étape" },
        { value: WorkflowActionTypes.CompleteWorkflow, name: 'Compléter le workflow' },
        { value: WorkflowActionTypes.SendMail, name: 'Envoyer un mail' },
        { value: WorkflowActionTypes.SendSms, name: 'Envoyer un SMS' },
        { value: WorkflowActionTypes.UpdateAttribute, name: 'Mettre à jour un attribut' },
        { value: WorkflowActionTypes.OpenUrl, name: 'ouvrir une URL' },
        { value: WorkflowActionTypes.SwitchFolderAsUnread, name: 'Passer le dossier en non lu' },
        { value: WorkflowActionTypes.ReminderCreation, name: 'Création de rappel' },
        { value: WorkflowActionTypes.ArchiveFolder, name: 'Archiver le dossier' },
    ];

    /**
     * Formulaire de l'action.
     */
    apiConfigurationActionForm: FormGroup;

    /**
     * Reference au composant dynamique affichant les données de l'action.
     */
    componentRef: ComponentRef<WorkflowDataInterface>;

    /**
     * Liste des éléments du menu contextuel.
     */
    contextMenuItems: MultiLevelContextMenuItem = null;

    /**
     * Instance du contrôle pour lequel est ouvert le menu contextuel.
     */
    currentContextMenuControl: AbstractControl;

    /**
     * Index du début du texte sélectionné dans le champ (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionStart: number;

    /**
     * Index de fin du texte sélectionné dans le champ sujet (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionEnd: number;

    /**
     * Position horizontale de la sourris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionX: number;

    /**
     * Position verticale de la sourris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionY: number;

    /**
     * Valeur indiquant si le menu contextuel est visible.
     */
    isContextMenuVisible: boolean;

    /**
     * Constructeur.
     * @param dialogRef
     * @param data
     * @param componentFactoryResolver
     * @param emailsTemplatesService
     * @param memoryService
     * @param attributesService
     * @param foldersStatesService
     * @param workflowStepDefinitionsService
     * @param workflowsService
     * @param spinner
     */
    constructor(
        public dialogRef: MatDialogRef<ActionFormDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ActionFormDialogData,
        private emailsTemplatesService: EmailsTemplatesService,
        private smsTemplatesService: SmsTemplatesService,
        protected memoryService: MemoryService,
        protected attributesService: AttributeDefinitionsService,
        protected foldersStatesService: FoldersStatesService,
        protected customersService: CustomersService,
        protected workflowDefinitionsService: WorkflowDefinitionsService,
        protected workflowsService: WorkflowDefinitionsService,
        protected apiConfigurationsService: RestApiConfigurationsService,
        private spinner: NgxSpinnerService
    ) {
        this.title = data.title;
        this.item = RestApiAction.clone(data.item);

        let variableMenuItems: MultiLevelContextMenuItem[] = data.variables.map((x) => {
            return new MultiLevelContextMenuItem(x.name, `{{data.Variables["${x.name}"]}}`);
        });

        this.contextMenuItems = new MultiLevelContextMenuItem('Root', null, variableMenuItems);

        // Charge les données de l'action (composant dynamique) lorsque la boite de dialogue est affichée.
        dialogRef.afterOpened().subscribe(() => {
            console.log('dialogRef.afterOpened', this.dynamicContainer);
            this.loadDataComponent(this.item.type);
        });

        this.apiConfigurationActionForm = new FormGroup({
            name: new FormControl(''),
            description: new FormControl(''),
            actionType: new FormControl(''),
        });

        if (data.readonly) {
            this.apiConfigurationActionForm.disable();
        }

        this.fillForm(this.item);
    }

    /**
     * Appellé lorsque l'utilisateur valide le formulaire.
     */
    onConfirm(): void {
        this.RetrieveValueFromForm();
        let result: ActionFormDialogResult = { status: true, item: this.item };

        // Close the dialog, return true
        this.dialogRef.close(result);
    }

    /**
     * Appellé lorsque l'utilisateur annule le formulaire.
     */
    onCancel(): void {
        let result: ActionFormDialogResult = { status: false, item: this.item };
        // Close the dialog, return false
        this.dialogRef.close(result);
    }

    /**
     * Appellé lorsque l'utilisateur change de type d'action.
     * @event : Type d'action selectionnée.
     */
    public onTypeChanged(event: MatSelectChange) {
        console.log('ActionFormDialogComponent - onTypeChanged', event);
        this.loadDataComponent(event.value as WorkflowActionTypes);
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur un champ de saisie.
     * @param event : Événement souris.
     */
    public onInputRightClick(event: MouseEvent) {
        this.isContextMenuVisible = true;

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        // Récupère la sélection dans le champ Input. Le paramètre remplacera cette selection.
        this.currentSelectionStart = event.target['selectionStart'];
        this.currentSelectionEnd = event.target['selectionEnd'];

        this.contextMenu.openMenu('dynamic', event);
    }

    /**
     * Ferme le menu contextuel.
     */
    public CloseContextMenu() {
        this.isContextMenuVisible = false;
    }

    /**
     * Levé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement clic du menu contextuel.
     */
    handleMenuItemClick(event: ContextMenuItemEvent) {
        if (this.currentContextMenuControl.value) {
            this.currentContextMenuControl.setValue(
                this.currentContextMenuControl.value.substring(0, this.currentSelectionStart) +
                    event.data +
                    this.currentContextMenuControl.value.substring(this.currentSelectionEnd)
            );
        } else {
            this.currentContextMenuControl.setValue(event.data);
        }

        this.isContextMenuVisible = false;
    }

    /**
     * Retourne les données de l'action à partir des champs du formulaire.
     */
    private RetrieveValueFromForm() {
        this.item.name = this.apiConfigurationActionForm.get('name').value;
        this.item.description = this.apiConfigurationActionForm.get('description').value;
        this.item.type = this.apiConfigurationActionForm.get('actionType').value;
        if (typeof this.componentRef.instance.data === 'string') {
            this.item.actionData = JSON.parse(this.componentRef.instance.data);
        } else {
            this.item.actionData = this.componentRef.instance.data;
        }
    }

    /**
     * Remplit les champs du formulaire avec les données de l'action.
     */
    private fillForm(item: RestApiAction) {
        this.apiConfigurationActionForm.get('name').setValue(item.name);
        this.apiConfigurationActionForm.get('description').setValue(item.description);
        this.apiConfigurationActionForm.get('actionType').setValue(item.type);
    }

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected dynamicComponentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Charge dynamique le composant de saisie des données de l'action.
     * @param type : Type de l'action.
     */
    private loadDataComponent(type: WorkflowActionTypes) {
        this.dynamicContainer.clear();
        this.dynamicComponentDestroyed$.next();

        if (type) {
            switch (type) {
                case WorkflowActionTypes.ChangeStepState:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionChangeStepStateDataComponent);
                    break;
                case WorkflowActionTypes.ActivateStep:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionActivateStepDataComponent);
                    break;
                case WorkflowActionTypes.SendMail:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionSendMailDataComponent);
                    break;
                case WorkflowActionTypes.SendSms:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionSendSmsDataComponent);
                    break;
                case WorkflowActionTypes.CompleteStep:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionCompleteStepDataComponent);
                    break;
                case WorkflowActionTypes.CompleteWorkflow:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionCompleteWorkflowDataComponent);
                    break;
                case WorkflowActionTypes.UpdateAttribute:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionUpdateAttributeDataComponent);
                    break;
                case WorkflowActionTypes.ApiCall:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionCallApiDataComponent);
                    break;
                case WorkflowActionTypes.SwitchFolderAsUnread:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionSwitchFolderAsUnreadDataComponent);
                    break;
                case WorkflowActionTypes.TimerStop:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionTimerStopComponent);
                    break;
                case WorkflowActionTypes.ReminderCreation:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionReminderCreationDataComponent);
                    break;

                case WorkflowActionTypes.ArchiveFolder:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionArchiveFolderDataComponent);
                    break;
                default:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowNotImplementedActionDataComponent);
                    console.log('ActionFormDialogComponent - loadDataComponent --> WorkflowNotImplementedActionDataComponent');
                    break;
            }

            this.componentRef.instance.contextualMenuSource.pipe(takeUntil(this.dynamicComponentDestroyed$)).subscribe((x) => {
                this.currentContextMenuControl = x.sourceControl;
                this.onInputRightClick(x.event);
            });

            if (this.data.readonly) {
                this.componentRef.instance.mode = FormMode.Reading;
            } else {
                this.componentRef.instance.mode = FormMode.Edition;
            }

            if (this.item.actionData) {
                this.componentRef.instance.data = JSON.stringify(this.item.actionData);
            }

            if (this.IsComponentNeedAttributes()) {
                this.loadAttributes(this.componentRef.instance as unknown as NeedAttributes);
            }

            if (this.IsComponentNeedEmailTemplates()) {
                this.loadEmailTemplates(this.componentRef.instance as unknown as NeedEmailTemplates);
            }

            if (this.IsComponentNeedSmsTemplates()) {
                this.loadSmsTemplates(this.componentRef.instance as unknown as NeedSmsTemplates);
            }

            if (this.IsComponentNeedFolderStates()) {
                this.loadFolderStates(this.componentRef.instance as unknown as NeedFolderStates);
            }

            if (this.IsComponentNeedCustomers()) {
                this.loadCustomers(this.componentRef.instance as unknown as NeedCustomers);
            }

            if (this.IsComponentNeedSteps()) {
                this.loadSteps(this.componentRef.instance as unknown as NeedSteps);
            }

            if (this.IsComponentNeedApiConfigurations()) {
                this.loadApiConfigurations(this.componentRef.instance as unknown as NeedApiConfigurations);
            }

            if (this.IsComponentNeedTimerTriggers()) {
                this.loadTimerTriggers(this.componentRef.instance as unknown as NeedTimerTriggers);
            }
        }
    }

    /**
     * Charge les étape de workflow et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadSteps(componentInstance: NeedSteps) {
        this.spinner.show('main-spinner');

        return this.workflowDefinitionsService.get(this.memoryService.getSelectedView().id).subscribe((workflow) => {
            componentInstance.steps = workflow.steps;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les attributs du workflow et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadAttributes(componentInstance: NeedAttributes) {
        this.spinner.show('main-spinner');
        this.attributesService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let attributes = x.value.sort((a, b) => {
                return a.header < b.header ? -1 : 1;
            });
            componentInstance.attributes = attributes;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les états de dossier et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadFolderStates(componentInstance: NeedFolderStates) {
        this.spinner.show('main-spinner');
        this.foldersStatesService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let folderStates = x.value.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.folderStates = folderStates;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les clients et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadCustomers(componentInstance: NeedCustomers) {
        this.spinner.show('main-spinner');
        this.customersService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let customers = x.value.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.customers = customers;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les modèles de mails et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadEmailTemplates(componentInstance: NeedEmailTemplates) {
        this.spinner.show('main-spinner');
        const viewContext: Context = {
            property: 'viewId',
            value: this.memoryService.getSelectedView().id,
        };
        const getOrganizationTemplates$ = this.emailsTemplatesService.List(null, null, null, null, 0, 1000);
        const getViewTemplates$ = this.emailsTemplatesService.List(viewContext, null, null, null, 0, 1000);

        forkJoin([getOrganizationTemplates$, getViewTemplates$]).subscribe((x) => {
            let templates = x[0].value.concat(x[1].value);
            templates = templates.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.templates = templates;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les modèles de SMS et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadSmsTemplates(componentInstance: NeedSmsTemplates) {
        this.spinner.show('main-spinner');
        const viewContext: Context = {
            property: 'viewId',
            value: this.memoryService.getSelectedView().id,
        };
        const getOrganizationTemplates$ = this.smsTemplatesService.List(null, null, null, null, 0, 1000);
        const getViewTemplates$ = this.smsTemplatesService.List(viewContext, null, null, null, 0, 1000);

        forkJoin([getOrganizationTemplates$, getViewTemplates$]).subscribe((x) => {
            let templates = x[0].value.concat(x[1].value);
            templates = templates.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.smsTemplates = templates;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les configurations d'API de la vue et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadApiConfigurations(componentInstance: NeedApiConfigurations) {
        this.spinner.show('main-spinner');
        this.apiConfigurationsService.List().subscribe((x) => {
            let configurations = x.value.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.apiConfigurations = configurations;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les Triggers de type timer du workflow et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadTimerTriggers(componentInstance: NeedTimerTriggers) {
        this.spinner.show('main-spinner');

        return this.workflowDefinitionsService.get(this.memoryService.getSelectedView().id).subscribe((workflow) => {
            componentInstance.timerTriggers = workflow.triggers.filter((x) => x.type === WorkflowTriggerTypes.Timer);
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des états de dossier.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedFolderStates() {
        // Vérifie que le composant possède la propriété 'folderStates'.
        return 'folderStates' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des clients.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedCustomers() {
        // Vérifie que le composant possède la propriété 'folderStates'.
        return 'customers' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des attributs.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedAttributes() {
        // Vérifie que le composant possède la propriété 'attributes'.
        return 'attributes' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des modèles de mail.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedEmailTemplates() {
        // Vérifie que le composant possède la propriété 'templates'.
        return 'templates' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des modèles de SMS.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedSmsTemplates() {
        // Vérifie que le composant possède la propriété 'templates'.
        return 'smsTemplates' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des étapes.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedSteps() {
        // Vérifie que le composant possède la propriété 'steps'.
        return 'steps' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des configurations d'API.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedApiConfigurations() {
        // Vérifie que le composant possède la propriété 'apiConfigurations'.
        return 'apiConfigurations' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des triggers de type 'Timer'.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedTimerTriggers() {
        // Vérifie que le composant possède la propriété 'timerTriggers'.
        return 'timerTriggers' in this.componentRef.instance;
    }
}
